import { TourProvider } from '@reactour/tour'
import * as Sentry from '@sentry/react'
import {
  QueryClient,
  QueryClientProvider
} from '@tanstack/react-query'
import history from 'history.js'
import moment from 'moment'
import { SnackbarProvider } from 'notistack'
import { Provider } from 'react-redux'
import { Router } from 'react-router-dom'
import '../styles/_app.scss'
import I18nConnectedProvider from '../translation/I18nConnectedProvider'
import AppContext from './appContext'
import Auth from './auth/Auth'
import { MaintenanceGuard } from './auth/MaintenanceGuard'
import { GlobalQueriesContextProvider } from './GlobalQueriesProvider'
import InactivityDetector from './InactivityDetector'
import AppLayout from './layout/AppLayout'
import { Store } from './redux/Store'
import routes from './RootRoutes'
import { CHTCtheme } from './theme/CHTCtheme'
import { CustomSnackbar } from './views/common-components/CustomSnackbars'

// TODO: Move to switching backends at some point
export const ACTIVE_BACKEND = 'Salesforce'
export const queryClient = new QueryClient()

Sentry.init({
  dsn: 'https://3c2ff33d208a8d5c5ff076f9bb8d6587@o4505963320311808.ingest.us.sentry.io/4507723798413312',
  environment: process.env.NODE_ENV,
  release: process.env.REACT_APP_VERSION,
  sendDefaultPii: true,

  integrations: [
    Sentry.browserTracingIntegration(),
    Sentry.replayIntegration(),
    Sentry.httpClientIntegration({
      failedRequestStatusCodes: [404, [400, 599]]
    })
  ],
  tracesSampleRate: 1.0,
  initialScope: {
    tags: {
      'app.startUrl': window.location.href
    }
  },
  // Set 'tracePropagationTargets' to control for which URLs distributed tracing should be enabled
  // tracePropagationTargets: ["localhost", /^https:\/\/centre.support\.io\/api/],
  replaysSessionSampleRate: 0.1,
  replaysOnErrorSampleRate: 1.0
})

// lock site for maintenance
moment.updateLocale('iu', {
  months: [
    'ᔭᓄᐊᕆ',
    'ᕕᕗᐊᕆ',
    'ᒫᔾᔨ',
    'ᐊᐃᕆᓕ',
    'ᒪᐃ',
    'ᔫᓂ',
    'ᔪᓚᐃ',
    'ᐊᐅᒍᓯ',
    'ᓯᑎᐱᕆ',
    'ᐅᑐᐱᕆ',
    'ᓄᕕᐱᕆ',
    'ᑎᓯᐱᕆ'
  ],
  monthsShort: [
    'ᔭᓄᐊᕆ',
    'ᕕᕗᐊᕆ',
    'ᒫᔾᔨ',
    'ᐊᐃᕆᓕ',
    'ᒪᐃ',
    'ᔫᓂ',
    'ᔪᓚᐃ',
    'ᐊᐅᒍᓯ',
    'ᓯᑎᐱᕆ',
    'ᐅᑐᐱᕆ',
    'ᓄᕕᐱᕆ',
    'ᑎᓯᐱᕆ'
  ],
  weekdays: ['ᓈᑦᑏᖑᔭᖅ', 'ᓇᒡᒐᔾᔭᐅ', 'ᐊᐃᑉᐹ', 'ᐱᖓᔪᐊ', 'ᕿᑎᖅᑰ', 'ᐅᓪᓗᑐᐃᓐᓇᖅ', 'ᓯᕙᑖᕐᕕᒃ'],
  weekdaysShort: [
    'ᓈᑦᑏᖑᔭᖅ',
    'ᓇᒡᒐᔾᔭᐅ',
    'ᐊᐃᑉᐹ',
    'ᐱᖓᔪᐊ',
    'ᕿᑎᖅᑰ',
    'ᐅᓪᓗᑐᐃᓐᓇᖅ',
    'ᓯᕙᑖᕐᕕᒃ'
  ]
})
moment.updateLocale('fr', {
  months: [
    'Janvier',
    'Février',
    'Mars',
    'Avril',
    'Mai',
    'Juin',
    'Juillet',
    'Août',
    'Septembre',
    'Octobre',
    'Novembre',
    'Décembre'
  ],
  monthsShort: [
    'Janvier',
    'Février',
    'Mars',
    'Avril',
    'Mai',
    'Juin',
    'Juillet',
    'Août',
    'Septembre',
    'Octobre',
    'Novembre',
    'Décembre'
  ],
  weekdaysShort: [
    'Dimanche',
    'Lundi',
    'Mardi',
    'Mercredi',
    'Jeudi',
    'Vendredi',
    'Samedi'
  ]
})
moment.updateLocale('en', { week: { dow: 1 } })
moment.updateLocale('en', {
  monthsShort: [
    'January',
    'February',
    'March',
    'April',
    'May',
    'June',
    'July',
    'August',
    'September',
    'October',
    'November',
    'December'
  ],
  weekdaysShort: [
    'Sunday',
    'Monday',
    'Tuesday',
    'Wednesday',
    'Thursday',
    'Friday',
    'Saturday'
  ]
})

const App = () => {
  return (
    <AppContext.Provider value={{ routes }}>
      <Provider store={Store}>
        <I18nConnectedProvider>
          <QueryClientProvider client={queryClient}>
            <SnackbarProvider
              Components={{
                success: CustomSnackbar,
                error: CustomSnackbar,
                warning: CustomSnackbar,
                info: CustomSnackbar
              }}
              maxSnack={3}
              autoHideDuration={2000}
            >
              <CHTCtheme>
                <TourProvider
                  steps={[]}
                  onClickClose={({ setIsOpen, setCurrentStep }) => {
                    setIsOpen(false)
                    setCurrentStep(0)
                  }}
                  styles={{
                    popover: base => ({
                      ...base,
                      '--reactour-accent': '#5cb7b7'
                    })
                  }}
                >
                  <Router history={history}>
                    <MaintenanceGuard>
                      <Auth>
                        <GlobalQueriesContextProvider>
                          <AppLayout />
                          <InactivityDetector />
                          {/* <MissingUserInformationPopUp /> */}
                        </GlobalQueriesContextProvider>
                      </Auth>
                    </MaintenanceGuard>

                  </Router>
                </TourProvider>
              </CHTCtheme>
            </SnackbarProvider>

          </QueryClientProvider>
        </I18nConnectedProvider>
      </Provider>
    </AppContext.Provider>
  )
}

export default App
